import {ErrorFlow} from 'olympe';
import {safeParseJSON} from './helpers';
import {LAMBDA_NAMES} from './constants';

/**
 * Extract each component of an address.
 * @param {string} address
 * @return {!Object}
 */
export const splitAddress = (address) => {
    const components = address.split(',');
    for (let i = 0; i < 5; i++) {
        components[i] = components[i] ? components[i].trim() : '';
    }
    return {
        street: components[0],
        postalCode: components[1],
        city: components[2],
        state: components[3],
        country: components[4],
    };
};

/**
 * Check if two addresses are the same
 * @param {DeliveryAddress} a
 * @param {DeliveryAddress} b
 * @return {boolean}
 */
export const isAddressIdentical = (a, b) => {
    if (!a || !b) {
        return false;
    }
    return a.name === b.name &&
        a.name2 === b.name2 &&
        a.houseNumber === b.houseNumber &&
        a.street === b.street &&
        a.postalCode === b.postalCode &&
        a.city === b.city &&
        a.state === b.state &&
        a.country === b.country;
};

/**
 *
 * @param {DeliveryAddress} deliveryAddress
 * @return {boolean}
 */
export const isAddressEmpty = (deliveryAddress) => {
    return !deliveryAddress.name &&
        !deliveryAddress.name2 &&
        !deliveryAddress.houseNumber &&
        !deliveryAddress.street &&
        !deliveryAddress.postalCode &&
        !deliveryAddress.city &&
        !deliveryAddress.state &&
        !deliveryAddress.country;
};

/**
 *
 * @type {Map<string, DeliveryAddress>}
 */
const fetchedAddresses = new Map();

export const fetchAddressFromStorageLocation = async (rawMDValue, context, plant, requesterId) => {
    const rawMDValueString = JSON.stringify(rawMDValue);
    if (fetchedAddresses.has(rawMDValueString)) {
        return fetchedAddresses.get(rawMDValueString);
    }
    const getAddressFromStorageLocation = context.get(LAMBDA_NAMES.GET_ADDRESS_FROM_STORAGE_LOCATION);
    const [
        controlFlow,
        storageLocationInput,
        plantInput,
        requesterIdIn,
    ] = getAddressFromStorageLocation.getInputs();

    const [
        Success,
        addressOutput,
        Error,
    ] = getAddressFromStorageLocation.getOutputs();

    const getAddressFromStorageLocationRunner = context.runner(getAddressFromStorageLocation)
        .set(storageLocationInput, rawMDValueString)
        .set(plantInput, plant.value)
        .set(requesterIdIn, requesterId);

    getAddressFromStorageLocationRunner.trigger(controlFlow);

    return Promise.any([
        getAddressFromStorageLocationRunner.waitFor(Success),
        getAddressFromStorageLocationRunner.waitFor(Error),
    ]).then((outputFlow) => {
        if (outputFlow instanceof ErrorFlow) {
            throw new Error(outputFlow.getMessage());
        } else {
            const address = getAddressFromStorageLocationRunner.get(addressOutput);
            const parsedAddress = safeParseJSON(address);
            const finalAddress = {
                name: parsedAddress ? parsedAddress.Name1 : '',
                name2: parsedAddress ? parsedAddress.Name2 : '',
                houseNumber: parsedAddress ? parsedAddress.House_Num1 : '',
                street: parsedAddress ? parsedAddress.Street : '',
                postalCode: parsedAddress ? parsedAddress.Post_Code1 : '',
                city: parsedAddress ? (parsedAddress.City1 || '') + ' ' + (parsedAddress.City2 || '') : '',
                state: parsedAddress ? parsedAddress.Region : '',
                country: parsedAddress ? parsedAddress.Country : '',
                manuallyModified: false,
            };
            fetchedAddresses.set(rawMDValueString, finalAddress);
            return finalAddress;
        }
    })
        .finally(() => getAddressFromStorageLocationRunner.destroy());
};

/**
 *
 * @type {Map<string, DeliveryAddress>}
 */
const fetchedPlantAddresses = new Map();

export const fetchAddressFromPlant = async (addressNumber, context) => {
    if (fetchedPlantAddresses.has(addressNumber)) {
        return fetchedPlantAddresses.get(addressNumber);
    }
    const getAddressFromPlant = context.get(LAMBDA_NAMES.GET_ADDRESS_FROM_PLANT);
    const [
        controlFlow,
        addressNumberInput,
    ] = getAddressFromPlant.getInputs();

    const [
        Success,
        addressOutput,
        Error,
    ] = getAddressFromPlant.getOutputs();

    const getAddressFromPlantRunner = context.runner(getAddressFromPlant)
        .set(addressNumberInput, addressNumber);

    getAddressFromPlantRunner.trigger(controlFlow);

    return Promise.any([
        getAddressFromPlantRunner.waitFor(Success),
        getAddressFromPlantRunner.waitFor(Error),
    ]).then((outputFlow) => {
        if (outputFlow instanceof ErrorFlow) {
            throw new Error(outputFlow.getMessage());
        } else {
            const address = getAddressFromPlantRunner.get(addressOutput);
            const parsedAddress = safeParseJSON(address); // can be undefined
            if (parsedAddress.length > 0) {
                const addr = parsedAddress.filter((item) => item.nation === '' || item.nation === ' ');
                const addrItem = addr.length > 0 ? addr[0] : parsedAddress[0];
                if ( addrItem ) {
                    const finalAddress = {
                        name: addrItem.name1,
                        name2: addrItem.name2,
                        houseNumber: addrItem.house_num1,
                        street: addrItem.street,
                        postalCode: addrItem.post_code1,
                        city: addrItem.city1,
                        state: addrItem.county,
                        country: addrItem.country,
                        manuallyModified: false,
                    };
                    fetchedPlantAddresses.set(addressNumber, finalAddress);
                    return finalAddress;
                }
            }
        }
    })
        .finally(() => getAddressFromPlantRunner.destroy());
};
